import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2 } from 'lucide-react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';

import TextInput from '../../components/ui/TextInput';
import TimeInput from '../../components/ui/TimeInput';
import CheckboxInput from '../../components/ui/CheckboxInput';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import Button from '../../components/ui/Button';
import { getLeaveVariations, getLeaveVariation } from '../../services/leaveVariationService';

// AKM POC §5.1 — yasal çerçeve (backend LegalFramework SAYI olarak serileşir): 0 = 4857 İş Kanunu,
// 1 = 657 Devlet Memurları Kanunu. Denkleştirme yalnız 4857'de, izinle karşılama yalnız 657'de
// (sunucu ikisini de 400 ile reddeder: balancingOnlyLabor / compensationLeaveOnlyCivilServant).
export const LABOR_4857 = 0;
export const CIVIL_SERVANT_657 = 1;
export const COMPENSATION_PAY = 0;
export const COMPENSATION_LEAVE = 1;
export const BALANCING_PERIODS = [0, 1, 2, 4];
// Kademe gün türleri — backend'in kanonik değerleri (boş = normalDay).
export const APPLIES_TO_VALUES = ['normalDay', 'weeklyRest', 'publicHoliday'];
// 657'de haftalık eşik boşsa önerilen değer: haftalık 40 saat (657 md.99).
export const CIVIL_SERVANT_WEEKLY_MINUTES = 2400;
const DEFAULT_MINUTES_PER_LEAVE_DAY = 480;

const EMPTY_TIER = { code: '', name: '', multiplier: '', appliesTo: 'normalDay', sortOrder: '' };

const toNumber = (v, fallback) => {
  if (v === '' || v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isNaN(n) ? fallback : n;
};

const variationOption = (v) => (v ? { id: v.id, name: v.name } : null);

function timeSpanToHHmm(ts) {
  if (!ts) return '';
  if (typeof ts === 'string') {
    const parts = ts.split(':');
    return parts.length >= 2 ? `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}` : ts;
  }
  return '';
}

function hhmmToTimeSpan(val) {
  if (!val) return '00:00:00';
  const parts = val.split(':');
  return `${(parts[0] || '00').padStart(2, '0')}:${(parts[1] || '00').padStart(2, '0')}:00`;
}

export default function OvertimePolicyDrawer({ open, onClose, initialValues, onSave }) {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [weeklyThresholdMinutes, setWeeklyThresholdMinutes] = useState('');
  const [dailyThresholdMinutes, setDailyThresholdMinutes] = useState('');
  const [annualOvertimeCapMinutes, setAnnualOvertimeCapMinutes] = useState('');
  const [nightStartTime, setNightStartTime] = useState('');
  const [nightEndTime, setNightEndTime] = useState('');
  const [nightPremiumPercentage, setNightPremiumPercentage] = useState('');
  const [weekStartDay, setWeekStartDay] = useState(1);
  const [tiers, setTiers] = useState([]);
  const [legalFramework, setLegalFramework] = useState(LABOR_4857);
  const [balancingPeriodMonths, setBalancingPeriodMonths] = useState(0);
  const [compensationType, setCompensationType] = useState(COMPENSATION_PAY);
  const [compensationLeaveVariationId, setCompensationLeaveVariationId] = useState(null);
  const [compensationMinutesPerLeaveDay, setCompensationMinutesPerLeaveDay] = useState(String(DEFAULT_MINUTES_PER_LEAVE_DAY));
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);

  const isCivilServant = legalFramework === CIVIL_SERVANT_657;
  const isLeaveCompensation = isCivilServant && compensationType === COMPENSATION_LEAVE;
  const compensationTypeMissing = isLeaveCompensation && !compensationLeaveVariationId;

  const legalFrameworkOptions = [
    { id: LABOR_4857, name: t('employeeStatus.labor4857') },
    { id: CIVIL_SERVANT_657, name: t('employeeStatus.civilServant657') },
  ];

  const balancingOptions = [
    { id: 0, name: t('overtimePolicy.balancingNone') },
    { id: 1, name: t('overtimePolicy.balancing1') },
    { id: 2, name: t('overtimePolicy.balancing2') },
    { id: 4, name: t('overtimePolicy.balancing4') },
  ];

  const compensationOptions = [
    { id: COMPENSATION_PAY, name: t('overtimePolicy.compensationPay') },
    { id: COMPENSATION_LEAVE, name: t('overtimePolicy.compensationLeave') },
  ];

  const appliesToOptions = [
    { id: 'normalDay', name: t('overtimePolicy.appliesToNormalDay') },
    { id: 'weeklyRest', name: t('overtimePolicy.appliesToWeeklyRest') },
    { id: 'publicHoliday', name: t('overtimePolicy.appliesToPublicHoliday') },
  ];

  // Karşılık izin türü seçicisi — kiracının izin türleri (uç sayfasız liste döner). api.js GET'te
  // reddetmez: sinyal res.data.status. Kimlikleri SABİT (useCallback) — AsyncGenericSelect her yeni
  // fonksiyonda aramayı yeniden kurar.
  const searchLeaveVariations = useCallback(async (q) => {
    const res = await getLeaveVariations();
    if (res?.data?.status !== true) return [];
    const result = res.data.data;
    const items = Array.isArray(result) ? result : (result?.data || []);
    const needle = (q || '').toLocaleLowerCase('tr-TR');
    return items
      .filter((v) => !needle || (v.name || '').toLocaleLowerCase('tr-TR').includes(needle))
      .map(variationOption);
  }, []);

  const getLeaveVariationById = useCallback(async (id) => {
    if (!id) return null;
    const res = await getLeaveVariation(id);
    return res?.data?.status === true ? variationOption(res.data.data) : null;
  }, []);

  const weekDayOptions = [
    { id: 1, name: t('overtimePolicy.monday') },
    { id: 2, name: t('overtimePolicy.tuesday') },
    { id: 3, name: t('overtimePolicy.wednesday') },
    { id: 4, name: t('overtimePolicy.thursday') },
    { id: 5, name: t('overtimePolicy.friday') },
    { id: 6, name: t('overtimePolicy.saturday') },
    { id: 7, name: t('overtimePolicy.sunday') },
  ];

  useEffect(() => {
    if (open) {
      if (initialValues) {
        setName(initialValues.name || '');
        setIsDefault(initialValues.isDefault || false);
        setWeeklyThresholdMinutes(initialValues.weeklyThresholdMinutes ?? '');
        setDailyThresholdMinutes(initialValues.dailyThresholdMinutes ?? '');
        setAnnualOvertimeCapMinutes(initialValues.annualOvertimeCapMinutes ?? '');
        setNightStartTime(timeSpanToHHmm(initialValues.nightStartTime));
        setNightEndTime(timeSpanToHHmm(initialValues.nightEndTime));
        setNightPremiumPercentage(initialValues.nightPremiumPercentage ?? '');
        setWeekStartDay(initialValues.weekStartDay ?? 1);
        setTiers((initialValues.tiers || []).map(tier => ({
          code: tier.code || '',
          name: tier.name || '',
          multiplier: tier.multiplier ?? '',
          // Boş gün türü sunucuda normalDay demektir; seçici boş görünmesin.
          appliesTo: tier.appliesTo || 'normalDay',
          sortOrder: tier.sortOrder ?? '',
        })));
        setLegalFramework(toNumber(initialValues.legalFramework, LABOR_4857));
        setBalancingPeriodMonths(toNumber(initialValues.balancingPeriodMonths, 0));
        setCompensationType(toNumber(initialValues.compensationType, COMPENSATION_PAY));
        setCompensationLeaveVariationId(initialValues.compensationLeaveVariationId || null);
        setCompensationMinutesPerLeaveDay(String(initialValues.compensationMinutesPerLeaveDay || DEFAULT_MINUTES_PER_LEAVE_DAY));
      } else {
        setName('');
        setIsDefault(false);
        setWeeklyThresholdMinutes('');
        setDailyThresholdMinutes('');
        setAnnualOvertimeCapMinutes('');
        setNightStartTime('22:00');
        setNightEndTime('06:00');
        setNightPremiumPercentage('');
        setWeekStartDay(1);
        setTiers([]);
        setLegalFramework(LABOR_4857);
        setBalancingPeriodMonths(0);
        setCompensationType(COMPENSATION_PAY);
        setCompensationLeaveVariationId(null);
        setCompensationMinutesPerLeaveDay(String(DEFAULT_MINUTES_PER_LEAVE_DAY));
      }
      setShowErrors(false);
    }
  }, [open, initialValues]);

  // Çerçeve değişince öbür çerçeveye ait alanlar sıfırlanır (gizli alanda kalan değer 400 doğurmasın):
  // 657 → denkleştirme yok, haftalık eşik boşsa 2400 önerilir; 4857 → karşılık ücret, izin türü yok.
  const handleLegalFrameworkChange = (val) => {
    const lf = toNumber(val, LABOR_4857);
    setLegalFramework(lf);
    if (lf === CIVIL_SERVANT_657) {
      setBalancingPeriodMonths(0);
      setWeeklyThresholdMinutes((prev) => (prev === '' || prev === null || prev === undefined
        ? String(CIVIL_SERVANT_WEEKLY_MINUTES)
        : prev));
    } else {
      setCompensationType(COMPENSATION_PAY);
      setCompensationLeaveVariationId(null);
    }
  };

  const handleCompensationTypeChange = (val) => {
    const ct = toNumber(val, COMPENSATION_PAY);
    setCompensationType(ct);
    if (ct !== COMPENSATION_LEAVE) setCompensationLeaveVariationId(null);
  };

  const handleAddTier = () => setTiers(prev => [...prev, { ...EMPTY_TIER }]);

  const handleRemoveTier = (idx) => setTiers(prev => prev.filter((_, i) => i !== idx));

  const handleTierChange = (idx, field, val) => {
    setTiers(prev => prev.map((tier, i) => i === idx ? { ...tier, [field]: val } : tier));
  };

  const handleSubmit = async () => {
    if (!name || !weeklyThresholdMinutes) return;
    // İzinle karşılamada izin türü zorunlu (sunucu: compensationLeaveTypeRequired). Çekmece açık kalır.
    if (compensationTypeMissing) {
      setShowErrors(true);
      return;
    }
    setSaving(true);
    const payload = {
      name,
      isDefault,
      weeklyThresholdMinutes: parseInt(weeklyThresholdMinutes) || 0,
      dailyThresholdMinutes: dailyThresholdMinutes !== '' ? parseInt(dailyThresholdMinutes) : null,
      annualOvertimeCapMinutes: annualOvertimeCapMinutes !== '' ? parseInt(annualOvertimeCapMinutes) : null,
      nightStartTime: hhmmToTimeSpan(nightStartTime),
      nightEndTime: hhmmToTimeSpan(nightEndTime),
      // Sunucu nullable: boş = gece primi yok (null gider, 0'a çevrilmez).
      nightPremiumPercentage: nightPremiumPercentage !== '' && nightPremiumPercentage !== null
        ? parseFloat(nightPremiumPercentage)
        : null,
      weekStartDay: parseInt(weekStartDay) || 1,
      legalFramework,
      balancingPeriodMonths: isCivilServant ? 0 : toNumber(balancingPeriodMonths, 0),
      compensationType: isCivilServant ? compensationType : COMPENSATION_PAY,
      compensationLeaveVariationId: isLeaveCompensation ? compensationLeaveVariationId : null,
      compensationMinutesPerLeaveDay: parseInt(compensationMinutesPerLeaveDay) || DEFAULT_MINUTES_PER_LEAVE_DAY,
      tiers: tiers
        .filter(tier => tier.code || tier.name)
        .map(tier => ({
          code: tier.code,
          name: tier.name,
          multiplier: parseFloat(tier.multiplier) || 1,
          appliesTo: tier.appliesTo || 'normalDay',
          sortOrder: parseInt(tier.sortOrder) || 0,
        })),
    };
    const success = await onSave(payload);
    if (success) onClose();
    setSaving(false);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size="50%"
      duration={150}
      overlayOpacity={0.2}
      style={{
        display: 'flex', flexDirection: 'column', maxWidth: '100vw', minWidth: 420,
        margin: '12px', borderRadius: '6px', height: 'calc(100vh - 24px)',
        boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)', zIndex: 1000,
      }}
    >
      <Header>
        <Title>{initialValues ? t('overtimePolicy.edit') : t('overtimePolicy.create')}</Title>
        <CloseBtn onClick={onClose}><X size={20} /></CloseBtn>
      </Header>

      <Body>
        <FormGrid>
          <FullWidth>
            <TextInput label={t('overtimePolicy.name')} value={name} onChange={e => setName(e.target.value)} required />
          </FullWidth>
          <FullWidth>
            <CheckboxInput label={t('overtimePolicy.isDefault')} checked={isDefault} onChange={setIsDefault} />
          </FullWidth>
          <FullWidth>
            <GenericSelect
              label={t('overtimePolicy.legalFramework')}
              options={legalFrameworkOptions}
              value={legalFramework}
              onChange={handleLegalFrameworkChange}
              clearable={false}
              required
            />
            {!isCivilServant && <Hint data-hint="legal-4857">{t('overtimePolicy.legalHint4857')}</Hint>}
          </FullWidth>
          <div>
            <TextInput label={t('overtimePolicy.weeklyThreshold')} type="number" value={weeklyThresholdMinutes} onChange={e => setWeeklyThresholdMinutes(e.target.value)} required min="0" />
            {isCivilServant && <Hint data-hint="weekly-657">{t('overtimePolicy.weeklyThresholdHint657')}</Hint>}
          </div>
          <TextInput label={t('overtimePolicy.dailyThreshold')} type="number" value={dailyThresholdMinutes} onChange={e => setDailyThresholdMinutes(e.target.value)} min="0" />
          <TextInput label={t('overtimePolicy.annualCap')} type="number" value={annualOvertimeCapMinutes} onChange={e => setAnnualOvertimeCapMinutes(e.target.value)} min="0" />
          <TextInput label={t('overtimePolicy.nightPremium')} type="number" value={nightPremiumPercentage} onChange={e => setNightPremiumPercentage(e.target.value)} min="0" max="100" />
          {!isCivilServant && (
            <FullWidth>
              <GenericSelect
                label={t('overtimePolicy.balancingPeriod')}
                options={balancingOptions}
                value={balancingPeriodMonths}
                onChange={(v) => setBalancingPeriodMonths(toNumber(v, 0))}
                clearable={false}
              />
            </FullWidth>
          )}
          {isCivilServant && (
            <FullWidth>
              <GenericSelect
                label={t('overtimePolicy.compensationType')}
                options={compensationOptions}
                value={compensationType}
                onChange={handleCompensationTypeChange}
                clearable={false}
              />
            </FullWidth>
          )}
          {isLeaveCompensation && (
            <>
              <AsyncGenericSelect
                label={t('overtimePolicy.compensationLeaveType')}
                value={compensationLeaveVariationId}
                onChange={(v) => { setCompensationLeaveVariationId(v || null); setShowErrors(false); }}
                onSearch={searchLeaveVariations}
                onGetById={getLeaveVariationById}
                required
                error={showErrors && compensationTypeMissing ? t('common.requiredField') : undefined}
              />
              <TextInput
                label={t('overtimePolicy.compensationMinutesPerLeaveDay')}
                type="number"
                value={compensationMinutesPerLeaveDay}
                onChange={e => setCompensationMinutesPerLeaveDay(e.target.value)}
                min="1"
              />
            </>
          )}
          <TimeInput label={t('overtimePolicy.nightStart')} value={nightStartTime} onChange={setNightStartTime} />
          <TimeInput label={t('overtimePolicy.nightEnd')} value={nightEndTime} onChange={setNightEndTime} />
          <FullWidth>
            <GenericSelect
              label={t('overtimePolicy.weekStartDay')}
              options={weekDayOptions}
              value={weekStartDay}
              onChange={setWeekStartDay}
            />
          </FullWidth>
        </FormGrid>

        <TierSection>
          <TierHeader>
            <TierTitle>{t('overtimePolicy.tiers')}</TierTitle>
            <AddTierBtn type="button" onClick={handleAddTier}>
              <Plus size={14} /> {t('overtimePolicy.addTier')}
            </AddTierBtn>
          </TierHeader>

          {tiers.length === 0 && <EmptyText>{t('overtimePolicy.noTiers')}</EmptyText>}

          {tiers.map((tier, idx) => (
            <TierRow key={idx}>
              <TierField>
                <TextInput label={t('overtimePolicy.tierCode')} value={tier.code} onChange={e => handleTierChange(idx, 'code', e.target.value)} />
              </TierField>
              <TierField>
                <TextInput label={t('overtimePolicy.tierName')} value={tier.name} onChange={e => handleTierChange(idx, 'name', e.target.value)} />
              </TierField>
              <TierFieldSmall>
                <TextInput label={t('overtimePolicy.tierMultiplier')} type="number" value={tier.multiplier} onChange={e => handleTierChange(idx, 'multiplier', e.target.value)} min="1" max="5" step="0.01" />
              </TierFieldSmall>
              <TierField>
                {/* Gün türü serbest metin DEĞİL: sunucu yalnız normalDay/weeklyRest/publicHoliday kabul eder. */}
                <GenericSelect
                  label={t('overtimePolicy.tierAppliesTo')}
                  options={appliesToOptions}
                  value={tier.appliesTo || 'normalDay'}
                  onChange={(v) => handleTierChange(idx, 'appliesTo', v || 'normalDay')}
                  clearable={false}
                />
              </TierField>
              <TierFieldSmall>
                <TextInput label={t('overtimePolicy.tierSortOrder')} type="number" value={tier.sortOrder} onChange={e => handleTierChange(idx, 'sortOrder', e.target.value)} min="0" />
              </TierFieldSmall>
              <RemoveBtn type="button" onClick={() => handleRemoveTier(idx)}><Trash2 size={14} /></RemoveBtn>
            </TierRow>
          ))}
        </TierSection>
      </Body>

      <Footer>
        <Button variant="outline" onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
        <Button onClick={handleSubmit} disabled={saving || !name || !weeklyThresholdMinutes}>
          {saving ? '...' : t('common.save')}
        </Button>
      </Footer>
    </Drawer>
  );
}

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px; border-bottom: 1px solid #E8ECF0;
`;
const Title = styled.h3`
  font-size: 16px; font-weight: 700; color: #004242; font-family: 'Nunito Sans', sans-serif; margin: 0;
`;
const CloseBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border: none; border-radius: 4px;
  background: none; color: #A0A8B4; cursor: pointer;
  &:hover { background: #F5F7FA; }
`;
const Body = styled.div`
  flex: 1; min-height: 0; overflow-y: auto; padding: 24px;
  display: flex; flex-direction: column; gap: 20px;
`;
const Hint = styled.p`
  font-family: 'Nunito Sans', sans-serif; font-size: 11px; line-height: 1.45;
  color: #64748b; margin: 4px 0 0;
`;
const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 12px;
  padding: 16px 24px; border-top: 1px solid #E8ECF0;
`;
const FormGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
`;
const FullWidth = styled.div`
  grid-column: 1 / -1;
`;
const TierSection = styled.div`
  border: 1.5px solid #E0E4E8; border-radius: 6px; padding: 16px;
`;
const TierHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;
`;
const TierTitle = styled.span`
  font-family: 'Nunito Sans', sans-serif; font-size: 13px; font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;
const AddTierBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  border: none; background: none;
  color: ${({ theme }) => theme.colors.primary.main};
  font-family: 'Nunito Sans', sans-serif; font-size: 12px; font-weight: 600;
  cursor: pointer; padding: 2px 0;
  &:hover { text-decoration: underline; }
`;
const EmptyText = styled.span`
  font-family: 'Nunito Sans', sans-serif; font-size: 12px; color: #A0A8B4;
`;
const TierRow = styled.div`
  display: flex; align-items: flex-end; gap: 8px; margin-bottom: 8px;
  flex-wrap: wrap;
`;
const TierField = styled.div`
  flex: 1; min-width: 120px;
`;
const TierFieldSmall = styled.div`
  flex: 0 0 90px;
`;
const RemoveBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border: none; border-radius: 4px;
  background: none; color: #A0A8B4; cursor: pointer; flex-shrink: 0;
  &:hover { background: rgba(229, 57, 53, 0.08); color: #E53935; }
`;
